import { BellRing } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { PtsBillingService, PtsProjectService } from "@/services/APIs";
import PTSNavigationRoutes from "../../../utils/navigationRoutes";
import { Card, CardContent } from "../../../components/ui/card";
import { resolveProjectLabel } from "./billingListUtils";
import {
  formatReminderAmount,
  getTomorrowDisplayDate,
  getTomorrowLatestReminder,
  INVOICE_REMINDER_QUERY_KEY,
} from "./invoiceReminderUtils";

const viewReminderLinkClass =
  "text-primary shrink-0 text-sm font-medium underline underline-offset-4 hover:text-primary/80";

interface InvoiceReminderCardProps {
  onOpenReminder?: (billingId: string) => void;
}

const InvoiceReminderCard = ({ onOpenReminder }: InvoiceReminderCardProps) => {
  const { data: reminders = [], isLoading } = useQuery({
    queryKey: INVOICE_REMINDER_QUERY_KEY,
    queryFn: () => PtsBillingService.getInvoiceReminders(),
  });

  const { data: projectOptions = [] } = useQuery({
    queryKey: ["open-project-list"],
    queryFn: () => PtsProjectService.getOpenProjectList(),
  });

  const projectSelectOptions = projectOptions.map(option => ({
    value: option.value,
    label: option.label,
  }));

  const tomorrowReminder = getTomorrowLatestReminder(reminders);
  const tomorrowLabel = getTomorrowDisplayDate();

  if (isLoading) {
    return null;
  }

  if (reminders.length === 0) {
    return null;
  }

  if (!tomorrowReminder) {
    return (
      <Link
        to={PTSNavigationRoutes.BILLING_INVOICE_REMINDERS}
        className={viewReminderLinkClass}
      >
        View reminder
      </Link>
    );
  }

  const handleCardClick = () => {
    onOpenReminder?.(tomorrowReminder.id);
  };

  return (
    <Card
      className="w-full cursor-pointer border border-amber-200 bg-gradient-to-r from-amber-50 to-white shadow-sm transition hover:border-amber-300 hover:shadow-md xl:max-w-xl"
      onClick={handleCardClick}
      role="button"
      tabIndex={0}
      onKeyDown={event => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          handleCardClick();
        }
      }}
    >
      <CardContent className="p-4">
        <div className="flex min-w-0 items-start gap-3">
          <div className="rounded-xl bg-amber-500/10 p-3">
            <BellRing className="h-5 w-5 text-amber-700" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex min-w-0 flex-wrap items-center gap-2">
                <p className="text-sm font-semibold text-amber-900">
                  Upcoming Reminder
                </p>
                {tomorrowReminder.invoiceNumber ? (
                  <span className="rounded-full border border-amber-300 bg-white px-2.5 py-0.5 text-xs font-semibold text-amber-900">
                    {tomorrowReminder.invoiceNumber}
                  </span>
                ) : null}
              </div>
              <span className="font-base rounded-b-md bg-amber-100 px-2 py-0.5 text-xs text-amber-800">
                Tomorrow · {tomorrowLabel}
              </span>
            </div>

            <div className="mt-1 space-y-1">
              <div className="flex items-center justify-start gap-3">
                <p className="text-primary min-w-0 truncate text-sm font-semibold">
                  {tomorrowReminder.client}
                </p>
                <p className="text-muted-foreground shrink-0 truncate text-xs">
                  {resolveProjectLabel(
                    tomorrowReminder.project,
                    projectSelectOptions
                  )}{" "}
                  · {formatReminderAmount(tomorrowReminder)}
                </p>
              </div>
              <div className="flex items-center justify-between gap-3">
                <p className="min-w-0 truncate text-xs text-amber-800">
                  Reminder on {tomorrowReminder.nextReminderDateDisplay}
                  {tomorrowReminder.reminderDays > 0
                    ? ` · Every ${tomorrowReminder.reminderDays} day${
                        tomorrowReminder.reminderDays === 1 ? "" : "s"
                      }`
                    : ""}
                </p>
                <Link
                  to={PTSNavigationRoutes.BILLING_INVOICE_REMINDERS}
                  className="text-primary hover:text-primary/80 shrink-0 text-xs font-medium underline underline-offset-4"
                  onClick={event => event.stopPropagation()}
                >
                  View more
                </Link>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default InvoiceReminderCard;
